import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AlertFacade } from '@curbnturf/alert';
import { BoondockFacade } from '@curbnturf/boondock-listing/src/lib/+state/boondock.facade';
import {
  CancellationPolicyOption,
  IBoondock,
  ICancellationPolicyOption,
  ICheckInProcessType,
  IListing,
  IReservationProcessType,
} from '@curbnturf/entities';
import {
  CheckedComponent,
  DisplayValidationErrorsDirective,
  EnumSelectorPipe,
  formatPolicyFormValue,
  IPolicyForm,
  policyFormBuilder,
} from '@curbnturf/form-helpers';
import { listingComplete, siteComplete } from '@curbnturf/helpers';
import { ListingRulesComponent } from '@curbnturf/host-forms/src/lib/listing-rules/listing-rules.component';
import { ListingFacade } from '@curbnturf/listing';
import { CancellationPolicyListComponent } from '@curbnturf/policy-components/src/lib/cancellation-policy-list/cancellation-policy-list.component';
import { IonicModule } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { ListingBookingComponent } from '../listing-booking/listing-booking.component';

@Component({
  selector: 'curbnturf-policies',
  templateUrl: './policies.component.html',
  styleUrls: ['./policies.component.scss'],
  standalone: true,
  imports: [
    CancellationPolicyListComponent,
    CheckedComponent,
    DisplayValidationErrorsDirective,
    EnumSelectorPipe,
    FormsModule,
    IonicModule,
    ListingRulesComponent,
    ListingBookingComponent,
    NgIf,
    NgSelectModule,
    NgFor,
    ReactiveFormsModule,
    RouterLink,
  ],
})
export class PoliciesComponent implements OnInit, OnDestroy, OnChanges {
  @Input() listing: IListing;
  @Input() boondock: IBoondock;

  bookingForm: FormGroup<{
    reservationProcess: FormControl<IReservationProcessType['type'] | null>;
    checkInProcess: FormControl<ICheckInProcessType['type'] | null>;
    checkIn: FormControl<string | null>;
    checkOut: FormControl<string | null>;
    nextReservationDiscount: FormControl<number | null>;
    minLengthOfStayInDays: FormControl<number | null>;
    maxLengthOfStayInDays: FormControl<number | null>;
    bookingWindow: FormControl<number | null>;
    reserveDaysInAdvance: FormControl<number | null>;
    cancellationPolicy: FormControl<ICancellationPolicyOption['option'] | null>;
  }>;

  policiesForm: FormGroup<{
    policies: FormArray<FormGroup<IPolicyForm>>;
  }>;

  siteComplete = siteComplete;
  listingComplete = listingComplete;

  cancellationPolicyOptions = CancellationPolicyOption;

  subscriptions: Subscription[] = [];

  get cancellationPolicy() {
    if (this.listing) {
      return this.bookingForm.controls.cancellationPolicy;
    }

    return;
  }

  get reservationProcess() {
    return this.bookingForm.controls.reservationProcess;
  }

  get checkInProcess() {
    const control = this.bookingForm.controls.checkInProcess;
    const value = control?.value;

    if (value === ('meetandgreet' as ICheckInProcessType['type'])) {
      control.setValue('meetAndGreet');
    }

    return control;
  }

  get reserveDaysInAdvance() {
    return this.bookingForm.controls.reserveDaysInAdvance;
  }

  get bookingWindow() {
    return this.bookingForm.controls.bookingWindow;
  }

  get minLengthOfStayInDays() {
    return this.bookingForm.controls.minLengthOfStayInDays;
  }

  get maxLengthOfStayInDays() {
    return this.bookingForm.controls.maxLengthOfStayInDays;
  }

  get checkIn() {
    return this.bookingForm.controls.checkIn;
  }

  get checkOut() {
    return this.bookingForm.controls.checkOut;
  }

  constructor(
    private fb: FormBuilder,
    private listingFacade: ListingFacade,
    private alertFacade: AlertFacade,
    private boondockFacade: BoondockFacade,
  ) {}

  ngOnInit() {
    this.createForms();
  }

  ngOnDestroy() {
    this.closeSubscriptions();
  }

  closeSubscriptions() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  ngOnChanges(): void {
    this.createForms();
  }

  createForms() {
    this.policiesForm = this.fb.group({
      policies: this.fb.array<FormGroup<IPolicyForm>>([]),
    });

    this.bookingForm = this.fb.group({
      cancellationPolicy: this.fb.control<ICancellationPolicyOption['option'] | null>(null, {
        updateOn: 'change',
      }),
      reservationProcess: this.fb.control<IReservationProcessType['type'] | null>('standard', {
        updateOn: 'change',
      }),
      checkInProcess: this.fb.control<ICheckInProcessType['type'] | null>('meetAndGreet', { updateOn: 'change' }),
      checkIn: this.fb.control<string | null>(null, {
        validators: [Validators.minLength(8), Validators.maxLength(8)],
        updateOn: 'change',
      }),
      checkOut: this.fb.control<string | null>(null, {
        validators: [Validators.minLength(8), Validators.maxLength(8)],
        updateOn: 'change',
      }),
      nextReservationDiscount: this.fb.control<number | null>(null, {
        validators: [Validators.min(0)],
        updateOn: 'change',
      }),
      minLengthOfStayInDays: this.fb.control<number | null>(null, {
        validators: [Validators.min(1), Validators.max(365)],
        updateOn: 'change',
      }),
      maxLengthOfStayInDays: this.fb.control<number | null>(null, {
        validators: [Validators.min(1)],
        updateOn: 'change',
      }),
      bookingWindow: this.fb.control<number | null>(365, { updateOn: 'change' }),
      reserveDaysInAdvance: this.fb.control<number | null>(null, {
        validators: [Validators.min(0), Validators.max(365)],
        updateOn: 'change',
      }),
    });

    if (this.listing?.id) {
      this.bookingForm.patchValue({
        cancellationPolicy: this.listing.cancellationPolicy,
        reservationProcess: this.listing.reservationProcess,
        checkInProcess: this.listing.checkInProcess,
        checkIn: this.listing.checkIn,
        checkOut: this.listing.checkOut,
        nextReservationDiscount: this.listing.nextReservationDiscount,
        minLengthOfStayInDays: this.listing.minLengthOfStayInDays,
        maxLengthOfStayInDays: this.listing.maxLengthOfStayInDays,
        bookingWindow: this.listing.bookingWindow,
        reserveDaysInAdvance: this.listing.reserveDaysInAdvance,
      });

      this.policiesForm.controls.policies.clear();
      const policies: FormGroup<IPolicyForm>[] | undefined = this.listing.policies?.map((policy) =>
        policyFormBuilder(this.fb, policy),
      );
      policies?.forEach((el) => this.policiesForm.controls.policies.push(el));
    }

    if (this.boondock?.id) {
      this.policiesForm.controls.policies.clear();
      const policies: FormGroup<IPolicyForm>[] | undefined = this.boondock.policies?.map((policy) =>
        policyFormBuilder(this.fb, policy),
      );
      policies?.forEach((el) => this.policiesForm.controls.policies.push(el));
    }

    // these are the only subscriptions so we can clear them before they are recreated.
    setTimeout(() => {
      this.closeSubscriptions();

      this.subscriptions.push(
        this.policiesForm.valueChanges
          .pipe(
            tap(() => {
              this.policiesForm.markAsTouched();
              this.policiesForm.markAsDirty();
            }),
            debounceTime(1000),
          )
          .subscribe(() => {
            this.updatePolicies();
          }),
        this.bookingForm.valueChanges
          .pipe(
            tap(() => {
              this.bookingForm.markAsTouched();
              this.bookingForm.markAsDirty();
              this.checkInProcess?.markAsTouched();
              this.checkInProcess?.markAsDirty();
              this.bookingWindow?.markAsTouched();
              this.bookingWindow?.markAsDirty();
              this.reservationProcess?.markAsTouched();
              this.reservationProcess?.markAsDirty();
              this.reserveDaysInAdvance?.markAsTouched();
              this.reserveDaysInAdvance?.markAsDirty();
              this.minLengthOfStayInDays?.markAsTouched();
              this.minLengthOfStayInDays?.markAsDirty();
              this.maxLengthOfStayInDays?.markAsTouched();
              this.maxLengthOfStayInDays?.markAsDirty();
              this.checkIn?.markAsTouched();
              this.checkIn?.markAsDirty();
              this.checkOut?.markAsTouched();
              this.checkOut?.markAsDirty();
            }),
            debounceTime(1000),
          )
          .subscribe(() => {
            this.updateBookingPolicy();
          }),
      );
    });
  }

  updatePolicies() {
    if (this.policiesForm.valid) {
      if (this.listing?.id) {
        // Update the user record with the following data
        this.listingFacade.updatePolicies(
          this.listing.id,
          this.policiesForm?.controls?.policies?.controls?.map((policy) => formatPolicyFormValue(policy)),
        );
      }

      if (this.boondock?.id) {
        this.boondock = {
          ...this.boondock,
          policies: this.policiesForm?.controls?.policies?.controls?.map((policy) => formatPolicyFormValue(policy)),
        };

        this.boondockFacade.update(this.boondock);
      }
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  updateBookingPolicy() {
    if (
      this.bookingForm.valid &&
      this.checkIn.value &&
      this.checkOut.value &&
      (this.reserveDaysInAdvance.value || this.reserveDaysInAdvance.value === 0) &&
      this.bookingWindow.value &&
      this.listing.id
    ) {
      // Update the user record with the following data
      this.listingFacade.updateBookingPolicy(this.listing.id, {
        cancellationPolicy: this.cancellationPolicy?.value as ICancellationPolicyOption['option'],
        checkIn: this.checkIn?.value,
        checkOut: this.checkOut?.value,
        minLengthOfStayInDays: this.minLengthOfStayInDays?.value || 1,
        maxLengthOfStayInDays: this.maxLengthOfStayInDays?.value || 14,
        reserveDaysInAdvance: this.reserveDaysInAdvance?.value,
        bookingWindow: this.bookingWindow?.value,
        checkInProcess: this.checkInProcess?.value as ICheckInProcessType['type'],
        reservationProcess: this.reservationProcess?.value as IReservationProcessType['type'],
      });
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }
}
